import { motion, useInView, AnimatePresence } from "framer-motion";
import { useRef, useState } from "react";
import { 
  Truck, Wrench, DollarSign, Hash, Calculator, Ship, Package,
  ExternalLink, Play, CheckCircle2, ArrowRight, Sparkles, Send, Loader2,
  Cloud, Monitor
} from "lucide-react";
import { Link } from "react-router-dom";
import { RevealOnScroll } from "../effects/AnimatedElements";
import LazyImage from "../LazyImage";
import { useLanguage } from "@/context/LanguageContext";

const products = [
  {
    id: "shipping",
    name: "Sistema de Envios",
    description: "Plataforma completa de embarque maritimo, rastreo online y recogida gratis. Contenedores USA a RD con seguro incluido.",
    image: "/products/envios.jpg",
    url: "https://mydominicanshipping.com",
    demoUrl: "https://globalmetaforademo.com/prueba-gratis",
    demoReady: true,
    icon: Truck,
    color: "#3b82f6",
    tag: "Envios USA-RD",
  },
  {
    id: "lametafora",
    name: "La Metafora Shipping",
    description: "Transporte y entrega confiable desde Estados Unidos hasta Republica Dominicana. Rastreo de paquetes en tiempo real.",
    image: "/products/lametafora.jpg",
    url: "https://lametafora.net",
    demoUrl: "https://globalmetaforademo.com/prueba-gratis",
    demoReady: true,
    icon: Ship,
    color: "#2563eb",
    tag: "Logistica",
  },
  {
    id: "metaforashipping",
    name: "Metafora Shipping",
    description: "Envios rapidos y seguros puerta a puerta en toda la Republica Dominicana. Maritimo y aereo con cobertura total.",
    image: "/products/metaforashipping.jpg",
    url: "https://metaforashipping.net",
    demoUrl: "https://globalmetaforademo.com/prueba-gratis",
    demoReady: true,
    icon: Package,
    color: "#ef4444",
    tag: "Envio y Logistica",
  },
  {
    id: "taller",
    name: "Sistema de Taller",
    description: "Gestion automotriz completa: ordenes de trabajo, inventario de repuestos, facturacion y portal de clientes.",
    image: "/products/taller.jpg",
    url: "https://mytripitaka.com",
    demoUrl: "https://demo-taller.globalmetafora.com/api/prueba-gratis",
    demoReady: true,
    icon: Wrench,
    color: "#ef4444",
    tag: "Automotriz",
  },
  {
    id: "prestamos",
    name: "Sistema de Prestamos",
    description: "Administra tu cartera de prestamos de forma eficiente. Control total sobre clientes, cobros y reportes financieros.",
    image: "/products/prestamos.jpg",
    url: "https://inversionmetafora.com",
    demoUrl: "https://inversionmetafora.com",
    demoReady: true,
    icon: DollarSign,
    color: "#10b981",
    tag: "Finanzas",
  },
  {
    id: "loteria",
    name: "Sistema de Loteria",
    description: "Control de bancas, sorteos, premios y agentes. Reportes en tiempo real y liquidacion automatica de ganancias.",
    image: null,
    url: null,
    demoUrl: "https://demo-loteria.globalmetafora.com/api/prueba-gratis",
    registerUrl: "https://demo-loteria.globalmetafora.com/api/trial/register",
    embedForm: true,
    demoReady: true,
    icon: Hash,
    color: "#f59e0b",
    tag: "Entretenimiento",
  },
  {
    id: "contabilidad",
    name: "Sistema de Contabilidad",
    description: "Gestion financiera completa: balance general, estados de resultados, cuentas por cobrar/pagar y reportes fiscales.",
    image: null,
    url: null,
    demoUrl: null,
    demoReady: false,
    icon: Calculator,
    color: "#8b5cf6",
    tag: "Contabilidad",
  },
];

const TrialForm = ({ product }) => {
  const [form, setForm] = useState({ nombre: "", email: "", telefono: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(product.registerUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setSuccess(true);
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || "Error al registrar. Intente de nuevo.");
      }
    } catch {
      setError("No se pudo conectar. El demo estara disponible pronto.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-2"
      >
        <CheckCircle2 size={28} className="mx-auto mb-2 text-[#10b981]" />
        <p className="text-white text-sm font-bold">Registro exitoso</p>
        <p className="text-[#94a3b8] text-xs mt-1">Revisa tu email para acceder</p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-2" data-testid={`trial-form-${product.id}`}>
      <input
        type="text"
        required
        placeholder="Tu nombre"
        value={form.nombre}
        onChange={(e) => setForm({ ...form, nombre: e.target.value })}
        className="w-full px-3 py-2 rounded-lg bg-[#111827] border border-white/10 text-white text-xs focus:border-[#f59e0b] focus:outline-none"
        data-testid="trial-input-nombre"
      />
      <input
        type="email"
        required
        placeholder="tu@email.com"
        value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
        className="w-full px-3 py-2 rounded-lg bg-[#111827] border border-white/10 text-white text-xs focus:border-[#f59e0b] focus:outline-none"
        data-testid="trial-input-email"
      />
      <input
        type="tel"
        placeholder="Telefono (opcional)"
        value={form.telefono}
        onChange={(e) => setForm({ ...form, telefono: e.target.value })}
        className="w-full px-3 py-2 rounded-lg bg-[#111827] border border-white/10 text-white text-xs focus:border-[#f59e0b] focus:outline-none"
        data-testid="trial-input-telefono"
      />
      {error && <p className="text-red-400 text-[10px]">{error}</p>}
      <motion.button
        type="submit"
        disabled={loading}
        className="w-full py-2.5 rounded-lg font-bold text-sm text-center flex items-center justify-center gap-2 text-white transition-all"
        style={{ backgroundColor: product.color }}
        whileHover={{ scale: 1.02, boxShadow: `0 0 20px ${product.color}50` }}
        whileTap={{ scale: 0.98 }}
        data-testid="trial-submit-btn"
      >
        {loading ? (
          <Loader2 size={14} className="animate-spin" />
        ) : (
          <>
            <Send size={14} />
            Probar Gratis 15 Dias
          </>
        )}
      </motion.button>
    </form>
  );
};

const ProductCard = ({ product, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  const Icon = product.icon;
  const { t } = useLanguage();

  return (
    <motion.div
      data-testid={`product-card-${product.id}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="group relative rounded-2xl overflow-hidden border border-white/5 bg-[#0a0f1e] hover:border-[#3b82f6]/30 transition-all duration-500"
    >
      {/* Screenshot Image */}
      <div className="relative h-52 overflow-hidden">
        {product.image ? (
          <LazyImage
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover object-top"
            animate={{ scale: isHovered ? 1.05 : 1 }}
            transition={{ duration: 0.5 }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#0a0f1e] to-[#111827]">
            <div className="text-center">
              <Icon size={48} className="mx-auto mb-3" style={{ color: product.color }} />
              <span className="text-xs text-[#94a3b8] uppercase tracking-widest font-semibold">Proximamente</span>
            </div>
          </div>
        )}

        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1e] via-transparent to-transparent" />

        {/* Tag */}
        <div className="absolute top-3 left-3">
          <span
            className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider text-white/90"
            style={{ backgroundColor: `${product.color}33`, border: `1px solid ${product.color}55` }}
          >
            {product.tag}
          </span>
        </div>

        {/* Free trial badge */}
        <div className="absolute top-3 right-3">
          <motion.span
            className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider flex items-center gap-1"
            style={{
              backgroundColor: product.demoReady ? "#10b98133" : "#f59e0b22",
              border: `1px solid ${product.demoReady ? "#10b98155" : "#f59e0b44"}`,
              color: product.demoReady ? "#10b981" : "#f59e0b",
            }}
            animate={product.demoReady ? { scale: [1, 1.05, 1] } : {}}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <CheckCircle2 size={10} />
            {product.demoReady ? t("products_demo_active") : t("products_soon")}
          </motion.span>
        </div>

        {/* Glow on hover */}
        <motion.div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{
            background: `radial-gradient(circle at center, ${product.color}10 0%, transparent 70%)`,
          }}
        />
      </div>

      {/* Content */}
      <div className="p-5">
        <div className="flex items-center gap-3 mb-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${product.color}15`, border: `1px solid ${product.color}30` }}
          >
            <Icon size={20} style={{ color: product.color }} />
          </div>
          <h3 className="text-white font-bold text-lg">{product.name}</h3>
        </div>

        <p className="text-[#94a3b8] text-sm leading-relaxed mb-3 line-clamp-2">
          {product.description}
        </p>

        {/* Version Cloud / Local */}
        <div className="mb-4 space-y-1.5" data-testid={`product-versions-${product.id}`}>
          <div className="flex items-start gap-2">
            <Cloud size={13} className="text-[#06b6d4] mt-0.5 shrink-0" />
            <p className="text-[11px] leading-snug">
              <span className="text-[#06b6d4] font-semibold">{t("version_cloud")}</span>
              <span className="text-[#64748b]"> {t("version_cloud_desc")}</span>
            </p>
          </div>
          <div className="flex items-start gap-2">
            <Monitor size={13} className="text-[#10b981] mt-0.5 shrink-0" />
            <p className="text-[11px] leading-snug">
              <span className="text-[#10b981] font-semibold">{t("version_local")}</span>
              <span className="text-[#64748b]"> {t("version_local_desc")}</span>
            </p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col gap-2">
          {product.embedForm ? (
            <TrialForm product={product} />
          ) : product.demoReady ? (
            <motion.a
              data-testid={`product-demo-${product.id}`}
              href={product.demoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full py-2.5 rounded-lg font-bold text-sm text-center flex items-center justify-center gap-2 text-white transition-all duration-300"
              style={{ backgroundColor: product.color }}
              whileHover={{ scale: 1.02, boxShadow: `0 0 20px ${product.color}50` }}
              whileTap={{ scale: 0.98 }}
            >
              <Play size={14} />
              {t("products_try_free")}
            </motion.a>
          ) : (
            <span
              data-testid={`product-demo-${product.id}`}
              className="w-full py-2.5 rounded-lg font-bold text-sm text-center flex items-center justify-center gap-2 border text-[#64748b]"
              style={{ borderColor: `${product.color}30` }}
            >
              {t("products_coming_soon")}
            </span>
          )}

          {/* Visit system link */}
          {product.url && (
            <motion.a
              data-testid={`product-link-${product.id}`}
              href={product.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 text-xs font-semibold text-[#64748b] hover:text-white transition-colors py-1"
              whileHover={{ x: 3 }}
            >
              <span>{t("products_view_production")}</span>
              <ExternalLink size={12} />
            </motion.a>
          )}
        </div>
      </div>

      {/* Bottom border glow on hover */}
      <motion.div
        className="absolute bottom-0 left-0 right-0 h-[2px]"
        style={{ background: `linear-gradient(90deg, transparent, ${product.color}, transparent)` }}
        initial={{ opacity: 0 }}
        animate={{ opacity: isHovered ? 1 : 0 }}
        transition={{ duration: 0.3 }}
      />
    </motion.div>
  );
};

const ProductsSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-80px" });
  const { t } = useLanguage();

  return (
    <section
      ref={sectionRef}
      data-testid="products-section"
      id="productos"
      className="relative py-24 overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#02040a] via-[#060c1a] to-[#02040a]" />

      {/* Subtle watermark */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none select-none opacity-[0.02]">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap text-[120px] font-black text-white -rotate-12">
          GLOBAL METAFORA APP
        </div>
      </div>

      {/* Glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[#3b82f6]/5 blur-[120px] rounded-full" />

      <div className="relative z-10 section-container">
        {/* Header */}
        <RevealOnScroll>
          <div className="text-center mb-16">
            <motion.span
              className="inline-block px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.3em] text-[#3b82f6] border border-[#3b82f6]/20 bg-[#3b82f6]/5 mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5 }}
            >
              Ecosistema de Productos
            </motion.span>

            <motion.h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              {t("products_title_1")}{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#3b82f6] to-[#06b6d4]">
                {t("products_title_2")}
              </span>
            </motion.h2>

            <motion.p
              className="text-[#94a3b8] text-lg max-w-2xl mx-auto"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {t("products_subtitle")}
            </motion.p>

            <motion.div
              className="flex items-center justify-center gap-3 mt-4"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <span className="text-[#10b981] text-sm font-semibold flex items-center gap-1.5">
                <CheckCircle2 size={14} />
                Prueba gratuita 15 dias en todos los sistemas
              </span>
            </motion.div>
          </div>
        </RevealOnScroll>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((product, index) => (
            <ProductCard key={product.id} product={product} index={index} />
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
        >
          <Link
            to="/demos"
            data-testid="view-all-demos"
            className="inline-flex items-center gap-2 px-8 py-3 rounded-xl bg-[#3b82f6] text-white font-bold text-sm hover:bg-[#2563eb] transition-colors"
          >
            <Sparkles size={16} />
            {t("products_view_all_demos")}
            <ArrowRight size={16} />
          </Link>
          <p className="text-[#64748b] text-sm mt-4">
            {t("products_tech_note")}{" "}
            <span className="text-[#3b82f6] font-semibold">Global Metafora APP</span>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export { products };
export default ProductsSection;
